import React, { useEffect, useState } from 'react';
import { App, Table, Button, Dropdown, Modal, Form, Input, Segmented, Select, Tag, Switch } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined, MoreOutlined, UndoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { UserOptionDto, normalizeUserOptions } from '../dto/reference';
import { getDefaultFinancialPermissionsForRole } from '../modules/users/userFinancialPermissionDefaults';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateEuropean } from '../utils/dateUtils';
import '../components/layout/AppPage.css';
import '../components/layout/ObjectCard.css';
import '../components/layout/ObjectCardExtras.css';
import './UserManagement.css';

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'red',
  OPERATIONS: 'blue',
  ORDER_STAFF: 'green',
  PARTNER: 'default',
  MARKETING: 'cyan',
};

const ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF', 'PARTNER', 'MARKETING'];

type UserManagementUser = UserOptionDto & {
  createdAt?: string;
  deletedAt?: string | null;
};

const UserManagement: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserManagementUser | null>(null);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users', { params: { status: statusFilter } });
      setUsers(normalizeUserOptions(response.data, `users.${statusFilter}`) as UserManagementUser[]);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'users:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshVisibleUsers = (nextVisibleStatus?: 'active' | 'archived' | 'all') => {
    if (nextVisibleStatus && nextVisibleStatus !== statusFilter) {
      setStatusFilter(nextVisibleStatus);
      return;
    }

    fetchUsers();
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const permissionPayload: Record<string, boolean> = {};
      ['canManageOrderCosts', 'canManagePurchaseInvoices', 'canViewMargin'].forEach((field) => {
        if (typeof values[field] === 'boolean') {
          permissionPayload[field] = values[field];
        }
      });
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          name: values.name,
          email: values.email,
          role: values.role,
          ...permissionPayload,
        });
        feedback.success(t('users:messages.updateSuccess', { email: values.email }));
      } else {
        await api.post('/users', { ...values, ...permissionPayload });
        feedback.success(t('users:messages.createSuccess', { email: values.email }));
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      refreshVisibleUsers(!editingUser && statusFilter === 'archived' ? 'active' : undefined);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'users:messages.saveFailed', undefined, { email: values.email })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user: UserManagementUser) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      canManageOrderCosts: user.canManageOrderCosts === true,
      canManagePurchaseInvoices: user.canManagePurchaseInvoices === true,
      canViewMargin: user.canViewMargin === true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (userId: string) => {
    const email = users.find((u) => u.id === userId)?.email;
    try {
      await api.delete(`/users/${userId}`);
      feedback.success(t('users:messages.deleteSuccess', { email }));
      fetchUsers();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'users:messages.deleteFailed', undefined, { email }));
    }
  };

  const handleRestore = async (userId: string) => {
    const email = users.find((u) => u.id === userId)?.email;
    try {
      await api.post(`/users/${userId}/restore`);
      feedback.success(t('users:messages.restoreSuccess', { email }));
      refreshVisibleUsers(statusFilter === 'archived' ? 'active' : undefined);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'users:messages.restoreFailed', undefined, { email }));
    }
  };

  const handleResetPassword = async (values: any) => {
    if (!resetUserId) return;
    const email = users.find((u) => u.id === resetUserId)?.email;
    setResetting(true);
    try {
      await api.put(`/users/${resetUserId}/reset-password`, {
        newPassword: values.newPassword,
      });
      feedback.success(t('users:messages.resetSuccess', { email }));
      setResetModalVisible(false);
      resetForm.resetFields();
      setResetUserId(null);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'users:messages.resetFailed', undefined, { email }));
    } finally {
      setResetting(false);
    }
  };

  const openResetModal = (userId: string) => {
    setResetUserId(userId);
    resetForm.resetFields();
    setResetModalVisible(true);
  };

  const handleRoleChange = (role: string) => {
    if (!editingUser) {
      form.setFieldsValue(getDefaultFinancialPermissionsForRole(role));
    }
  };

  // Archiving asks for confirmation first; a restore does not.
  const confirmArchive = (user: UserManagementUser) => {
    modal.confirm({
      title: t('users:confirmDelete'),
      content: user.email,
      okText: t('common:yes'),
      cancelText: t('common:no'),
      okButtonProps: { danger: true },
      onOk: () => handleDelete(user.id),
    });
  };

  const rowMenuItems = (user: UserManagementUser): MenuProps['items'] => (user.deletedAt
    ? [{ key: 'restore', icon: <UndoOutlined />, label: t('users:restoreAction') }]
    : [
      { key: 'edit', icon: <EditOutlined />, label: t('common:edit') },
      { key: 'reset', icon: <LockOutlined />, label: t('users:resetPassword') },
      ...(user.id !== currentUser?.id
        ? [{ type: 'divider' as const }, { key: 'archive', icon: <DeleteOutlined />, danger: true, label: t('users:archiveAction') }]
        : []),
    ]);

  const columns: ColumnsType<UserManagementUser> = [
    {
      title: '',
      key: 'menu',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_: unknown, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: rowMenuItems(record),
            onClick: ({ key }) => {
              if (key === 'edit') handleEdit(record);
              if (key === 'reset') openResetModal(record.id);
              if (key === 'archive') confirmArchive(record);
              if (key === 'restore') void handleRestore(record.id);
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label={t('users:rowMenu')} />
        </Dropdown>
      ),
    },
    {
      title: t('users:name'),
      dataIndex: 'name',
      key: 'name',
      // The only flexible column: it takes the width the fixed ones leave.
      render: (name: string, record) => (
        <div className="users-page__identity">
          <span className="users-page__name-line">
            {record.deletedAt ? (
              <span className="users-page__name" title={name}>{name}</span>
            ) : (
              <button type="button" className="users-page__name users-page__name--link" title={name} onClick={() => handleEdit(record)}>
                {name}
              </button>
            )}
            {record.id === currentUser?.id && (
              <Tag color="blue">{t('users:you')}</Tag>
            )}
          </span>
          <span className="users-page__meta" title={record.email}>{record.email}</span>
        </div>
      ),
    },
    {
      title: t('users:role'),
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'} className="users-page__tag" title={t(`common:userRole.${role}`)}>
          {t(`common:userRole.${role}`)}
        </Tag>
      ),
    },
    {
      title: t('users:statusLabel'),
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 120,
      render: (deletedAt?: string | null) => (
        <Tag color={deletedAt ? 'default' : 'success'}>
          {deletedAt ? t('users:status.archived') : t('users:status.active')}
        </Tag>
      ),
    },
    {
      title: t('users:financialPermissions.label'),
      key: 'financialPermissions',
      width: 220,
      render: (_: unknown, record) => (
        <span className="users-page__tags">
          {record.canManageOrderCosts && <Tag>{t('users:financialPermissions.orderCostsShort')}</Tag>}
          {record.canManagePurchaseInvoices && <Tag color="gold">{t('users:financialPermissions.purchaseInvoicesShort')}</Tag>}
          {record.canViewMargin && <Tag color="green">{t('users:financialPermissions.marginShort')}</Tag>}
          {!record.canManageOrderCosts && !record.canManagePurchaseInvoices && !record.canViewMargin && (
            <span className="users-page__meta">—</span>
          )}
        </span>
      ),
    },
    {
      title: t('users:created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      className: 'users-page__date',
      render: (date: string) => formatDateEuropean(date),
    },
  ];

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'OPERATIONS',
      ...getDefaultFinancialPermissionsForRole('OPERATIONS'),
    });
    setModalVisible(true);
  };

  return (
    <div className="app-page users-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <div className="app-page__toolbar-group">
            <Segmented<'active' | 'archived' | 'all'>
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'active', label: t('users:statusOptions.active') },
                { value: 'archived', label: t('users:statusOptions.archived') },
                { value: 'all', label: t('users:statusOptions.all') },
              ]}
              aria-label={t('users:statusLabel')}
            />
            <span className="app-page__count">{users.length}</span>
          </div>
          <div className="app-page__toolbar-actions">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t('users:addUser')}
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          tableLayout="fixed"
        />
      </section>

      {/* Create / Edit User Modal */}
      <Modal
        title={editingUser ? t('users:editUser') : t('users:addUser')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        maskClosable={false}
        footer={null}
        width={500}
        className="obj-drawer"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={() => feedback.warning(t('users:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="name"
            label={t('users:name')}
            rules={[{ required: true, message: t('users:validation.nameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('users:email')}
            rules={[
              { required: true, message: t('users:validation.emailRequired') },
              { type: 'email', message: t('users:validation.emailInvalid') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label={t('users:role')}
            rules={[{ required: true, message: t('users:validation.roleRequired') }]}
          >
            <Select onChange={handleRoleChange}>
              {ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {t(`common:userRole.${role}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label={t('users:password')}
              rules={[
                { required: true, message: t('users:validation.passwordRequired') },
                { min: 6, message: t('users:validation.passwordMin') },
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item label={t('users:financialPermissions.label')}>
            <div className="users-page__permissions">
              <label className="tool-switch-line">
                <Form.Item name="canManageOrderCosts" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
                <span>{t('users:financialPermissions.orderCosts')}</span>
              </label>
              <label className="tool-switch-line">
                <Form.Item name="canManagePurchaseInvoices" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
                <span>{t('users:financialPermissions.purchaseInvoices')}</span>
              </label>
              <label className="tool-switch-line">
                <Form.Item name="canViewMargin" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
                <span>{t('users:financialPermissions.margin')}</span>
              </label>
            </div>
          </Form.Item>
          <div className="users-page__modal-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingUser(null);
                form.resetFields();
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingUser ? t('common:save') : t('users:addUser')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={t('users:resetPassword')}
        open={resetModalVisible}
        onCancel={() => {
          setResetModalVisible(false);
          setResetUserId(null);
          resetForm.resetFields();
        }}
        maskClosable={false}
        footer={null}
        width={400}
        className="obj-drawer"
      >
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword} onFinishFailed={() => feedback.warning(t('users:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="newPassword"
            label={t('users:newPassword')}
            rules={[
              { required: true, message: t('users:validation.passwordRequired') },
              { min: 6, message: t('users:validation.passwordMin') },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <div className="users-page__modal-actions">
            <Button type="primary" htmlType="submit" loading={resetting}>
              {t('users:resetPassword')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
